// Voice Recorder Hook - Uses MediaRecorder API for better browser compatibility
// Works with Brave, Chrome, Firefox, Safari, and Edge

import { useState, useRef, useCallback, useEffect } from 'react';
import { getAuthHeaders } from 'src/utils/auth-headers';

interface UseVoiceRecorderOptions {
  onTranscript: (text: string) => void;
  onError?: (error: string) => void;
}

interface UseVoiceRecorderReturn {
  isRecording: boolean;
  isProcessing: boolean;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  isSupported: boolean;
  audioLevel: number;
}

// Transcription APIs are now handled server-side for security

export function useVoiceRecorder({
  onTranscript,
  onError
}: UseVoiceRecorderOptions): UseVoiceRecorderReturn {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationRef = useRef<number | null>(null);

  const isSupported = typeof navigator !== 'undefined' &&
    navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === 'function';

  // Cleanup function
  const cleanup = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current = null;
    }
    analyserRef.current = null;
    setAudioLevel(0);
  }, []);

  // Monitor audio levels for visual feedback
  const monitorAudioLevel = useCallback(() => {
    if (!analyserRef.current) return;

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);

    const updateLevel = () => {
      if (!analyserRef.current || !isRecording) return;

      analyserRef.current.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
      setAudioLevel(average / 255);

      animationRef.current = requestAnimationFrame(updateLevel);
    };

    updateLevel();
  }, [isRecording]);

  // Helper to convert blob to base64
  const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = (reader.result as string).split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  // Transcribe audio using Groq's Whisper API via secure server proxy
  const transcribeWithGroq = async (audioBlob: Blob): Promise<string> => {
    const audioData = await blobToBase64(audioBlob);

    const response = await fetch('/api/transcribe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({
        provider: 'groq',
        audioData,
        mimeType: audioBlob.type
      })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error?.message || 'Transcription failed');
    }

    const result = await response.json();
    return result.text || '';
  };

  // Transcribe using Deepgram via secure server proxy
  const transcribeWithDeepgram = async (audioBlob: Blob): Promise<string> => {
    const audioData = await blobToBase64(audioBlob);

    const response = await fetch('/api/transcribe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({
        provider: 'deepgram',
        audioData,
        mimeType: audioBlob.type
      })
    });

    if (!response.ok) {
      throw new Error('Deepgram transcription failed');
    }

    const result = await response.json();
    return result.results?.channels?.[0]?.alternatives?.[0]?.transcript || '';
  };

  // Try browser's native speech recognition first (for Chrome/Edge)
  const tryNativeSpeechRecognition = useCallback((): Promise<string> => {
    return new Promise((resolve, reject) => {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

      if (!SpeechRecognition) {
        reject(new Error('Native speech recognition not available'));
        return;
      }

      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'en-US';

      let finalTranscript = '';

      recognition.onresult = (event: any) => {
        finalTranscript = event.results[0][0].transcript;
      };

      recognition.onend = () => {
        resolve(finalTranscript);
      };

      recognition.onerror = (event: any) => {
        reject(new Error(event.error));
      };

      recognition.start();

      // Auto-stop after 10 seconds
      setTimeout(() => {
        try {
          recognition.stop();
        } catch (e) {
          // Ignore
        }
      }, 10000);
    });
  }, []);

  const startRecording = useCallback(async () => {
    if (isRecording || !isSupported) return;

    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      streamRef.current = stream;

      // Set up audio analysis for visual feedback
      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      // Determine best audio format
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : MediaRecorder.isTypeSupported('audio/mp4')
            ? 'audio/mp4'
            : 'audio/wav';

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        setIsRecording(false);
        setIsProcessing(true);

        try {
          const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });

          let transcript = '';

          // Try Groq Whisper first via secure server proxy (fastest and free)
          try {
            transcript = await transcribeWithGroq(audioBlob);
          } catch (e) {
            // console.warn('Groq transcription failed, trying alternatives...', e);
          }

          // Fallback to Deepgram via secure server proxy
          if (!transcript) {
            try {
              transcript = await transcribeWithDeepgram(audioBlob);
            } catch (e) {
              // console.warn('Deepgram transcription failed', e);
            }
          }

          if (transcript) {
            onTranscript(transcript);
          } else {
            onError?.('Could not transcribe audio. Please try again or type your message.');
          }
        } catch (error) {
          // console.error('Transcription error:', error);
          onError?.('Failed to process audio. Please try again.');
        } finally {
          setIsProcessing(false);
          cleanup();
        }
      };

      // Start recording
      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);
      monitorAudioLevel();

      // Auto-stop after 30 seconds
      setTimeout(() => {
        if (mediaRecorderRef.current?.state === 'recording') {
          stopRecording();
        }
      }, 30000);

    } catch (error) {
      // console.error('Failed to start recording:', error);
      onError?.('Microphone access denied. Please allow microphone access and try again.');
      cleanup();
    }
  }, [isRecording, isSupported, onTranscript, onError, cleanup, monitorAudioLevel]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    isRecording,
    isProcessing,
    startRecording,
    stopRecording,
    isSupported,
    audioLevel
  };
}

// Text-to-speech utility with better voice selection
export function speakText(text: string, enabled: boolean = true): Promise<void> {
  return new Promise((resolve) => {
    if (!enabled || typeof window === 'undefined' || !window.speechSynthesis) {
      resolve();
      return;
    }

    // Cancel any ongoing speech
    window.speechSynthesis.cancel();

    // Clean up text
    const cleanText = text
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Remove markdown links
      .replace(/[✅•✨🏠📧📱🎉👍🏗️🏢📞💡🔧⚡]/g, '') // Remove emojis
      .replace(/\*\*([^*]+)\*\*/g, '$1') // Remove bold markdown
      .replace(/\n+/g, '. ') // Replace newlines with pauses
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim();

    if (!cleanText) {
      resolve();
      return;
    }

    const utterance = new SpeechSynthesisUtterance(cleanText);
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;

    // Get voices and select the best one
    const setVoice = () => {
      const voices = window.speechSynthesis.getVoices();

      // Prefer these voices in order
      const preferredVoices = [
        'Google US English',
        'Microsoft Zira',
        'Samantha',
        'Alex',
        'Karen',
        'Daniel'
      ];

      for (const preferred of preferredVoices) {
        const voice = voices.find(v => v.name.includes(preferred));
        if (voice) {
          utterance.voice = voice;
          break;
        }
      }

      // Fallback to first English voice
      if (!utterance.voice) {
        const englishVoice = voices.find(v => v.lang.startsWith('en'));
        if (englishVoice) {
          utterance.voice = englishVoice;
        }
      }
    };

    // Voices may load async
    if (window.speechSynthesis.getVoices().length > 0) {
      setVoice();
    } else {
      window.speechSynthesis.onvoiceschanged = setVoice;
    }

    utterance.onend = () => resolve();
    utterance.onerror = () => resolve();

    window.speechSynthesis.speak(utterance);
  });
}

export function stopSpeaking(): void {
  if (typeof window !== 'undefined' && window.speechSynthesis) {
    window.speechSynthesis.cancel();
  }
}
